import React, { useState, useEffect, useMemo, useRef } from "react";
import { Form, Button, Tabs, Tab, Col, Row } from "react-bootstrap";
import RecipeCard from "./RecipeCard";
import RecipeFilter from "./RecipeFilter";
import RecipeForm from "./RecipeForm";
import "styles/ChemicalRecipes.css";
import { useSelector, useDispatch } from "react-redux";
import ShareRecipes from "./ShareRecipes";
import RecipeService from "services/RecipeService";
import CardSkeleton from "components/common/CardSkeleton";
import EmptyChemicalRecipes from "./EmptyChemicalRecipes";
import {
  NO_SHARED_RECIPES,
  SHARED_RECIPES_WITH,
  RECIPE_FETCH_ERROR,
  UPDATED_RECIPE,
  ADDED_RECIPE,
  CHEMICAL_FETCH_ERROR,
  RECIPE_TYPE_FETCH_ERROR,
  LABEL_SEARCH_RECIPE,
  NO_ARCHIVED_RECIPES,
  NEW_VERSION_FOR_RECIPE,
} from "configurations/MessageConstants";
import SuccessPopup from "components/common/SuccessPopup";
import {
  setSaveRecipeSuccess,
  setShareRecipeSuccess,
  setRecipes,
  setChemicals,
  setRecipeTypes,
  setEditedRecipe,
} from "reduxStore/slices/recipeSlice";
import { debounce } from "lodash";
import CustomPagination from "components/common/CustomPagination";
import { MODE_TYPES, SHARE_TYPES, TAB_KEYS } from "configurations/Constants";
import EmptyShare from "components/common/EmptyShare";
import { DateHelper } from "utils/DateHelper";
import EmptyArchive from "./EmptyArchive";

const ChemicalRecipes = () => {
  const dispatch = useDispatch();
  const recipes = useSelector((state) => state.recipe.recipes);
  const abortControllerRef = useRef(null);
  const [showFilter, setShowFilter] = useState(false);
  const [showRecipeForm, setShowRecipeForm] = useState(false);
  const [showShareRecipe, setShowShareRecipe] = useState(false);
  const [selectedYear, setSelectedYear] = useState("2025");
  const [selectedChemicals, setSelectedChemicals] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_KEYS.OWNED_RECIPES);
  const [myRecipesCurrentPage, setMyRecipesCurrentPage] = useState(1);
  const [sharedRecipesCurrentPage, setSharedRecipesCurrentPage] = useState(1);
  const [archivedRecipesCurrentPage, setArchivedRecipesCurrentPage] =
    useState(1);
  const [paginatedMyRecipes, setPaginatedMyRecipes] = useState([]);
  const [paginatedSharedRecipes, setPaginatedSharedRecipes] = useState([]);
  const [paginatedArchivedRecipes, setPaginatedArchivedRecipes] = useState([]);
  const savedRecipe = useSelector((state) => state.recipe.savedRecipe);
  const recipeMode = useSelector((state) => state.recipe.recipeMode);
  const saveRecipeSuccess = useSelector(
    (state) => state.recipe.saveRecipeSuccess
  );
  const sharedRecipeInfo = useSelector(
    (state) => state.recipe.sharedRecipeInfo
  );
  const shareRecipeSuccess = useSelector(
    (state) => state.recipe.shareRecipeSuccess
  );

  const sharedRecipes = useMemo(
    () =>
      recipes?.filter(
        (recipe) => recipe.id && recipe.isShared && !recipe.archived
      ) || [],
    [recipes]
  );
  const myRecipes = useMemo(
    () =>
      recipes?.filter(
        (recipe) => recipe.id && !recipe.isShared && !recipe.archived
      ) || [],
    [recipes]
  );
  const archivedRecipes = useMemo(
    () => recipes?.filter((recipe) => recipe.id && recipe.archived) || [],
    [recipes]
  );

  useEffect(() => {
    getRecipes();
    setMyRecipesCurrentPage(1);
    setSharedRecipesCurrentPage(1);
    setArchivedRecipesCurrentPage(1);
  }, [
    dispatch,
    debouncedSearch,
    selectedChemicals,
    selectedTypes,
    selectedYear,
  ]);

  useEffect(() => {
    getChemicals();
    getRecipeTypes();
  }, [dispatch]);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((searchValue) => {
        setDebouncedSearch(searchValue);
      }, 400),
    []
  );

  useEffect(() => {
    debouncedSetSearch(search);
  }, [search, debouncedSetSearch]);

  const getRecipes = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsLoading(true);
    let params = `search=${debouncedSearch}&year=${selectedYear}`;

    if (selectedChemicals && selectedChemicals.length > 0) {
      const chemicalNames = selectedChemicals
        .map((chemical) => chemical.value)
        .join(",");
      params += `&chemicalName=${chemicalNames}`;
    }

    if (selectedTypes && selectedTypes.length > 0) {
      const recipeTypes = selectedTypes.map((type) => type.value).join(",");
      params += `&recipeType=${recipeTypes}`;
    }

    try {
      const response = await RecipeService.getRecipes(
        params,
        abortController.signal
      );
      if (!abortController.signal.aborted) {
        dispatch(setRecipes(response.items));
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        console.error(RECIPE_FETCH_ERROR, error);
      }
    } finally {
      if (
        abortControllerRef.current === abortController &&
        !abortController.signal.aborted
      ) {
        setIsLoading(false);
      }
    }
  };

  const getChemicals = async () => {
    setIsLoading(true);
    try {
      const response = await RecipeService.getChemicals();
      dispatch(setChemicals(response.items));
    } catch (error) {
      console.error(CHEMICAL_FETCH_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRecipeTypes = async () => {
    setIsLoading(true);
    try {
      const response = await RecipeService.getRecipeTypes();
      dispatch(setRecipeTypes(response.items));
    } catch (error) {
      console.error(RECIPE_TYPE_FETCH_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessClose = () => {
    dispatch(setSaveRecipeSuccess(false));
  };

  const renderShareRecipeSuccess = () => {
    const { success, details, farmerName } = sharedRecipeInfo;
    if (!success.length && !details.length) return null;
    return (
      <>
        {success.length > 0 && (
          <div className="mb-2">
            <span>
              {SHARED_RECIPES_WITH} {farmerName}:{" "}
            </span>
            <span className="fw-bold">
              {success.map((recipe) => recipe.recipeName || recipe).join(", ")}
            </span>
          </div>
        )}
        {details.length > 0 && (
          <div>
            {details.map((detail, index) => (
              <div className="mb-2" key={detail.reason + index}>
                {detail.reason}:{" "}
                <span className="fw-bold">{detail.recipeNames.join(", ")}</span>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const handleApplyFilters = (filters) => {
    setSelectedChemicals(filters.selectedChemicals);
    setSelectedTypes(filters.selectedTypes);
    const hasFilters =
      filters.selectedChemicals.length > 0 || filters.selectedTypes.length > 0;
    setIsFilterApplied(hasFilters);
  };

  const handleMyRecipesPageChange = (page) => {
    setMyRecipesCurrentPage(page);
  };

  const handleSharedRecipesPageChange = (page) => {
    setSharedRecipesCurrentPage(page);
  };

  const handleArchivedRecipesPageChange = (page) => {
    setArchivedRecipesCurrentPage(page);
  };

  const handleTabSelect = (tabKey) => {
    setActiveTab(tabKey);
    if (tabKey === TAB_KEYS.OWNED_RECIPES) {
      setMyRecipesCurrentPage(1);
    } else if (tabKey === TAB_KEYS.SHARED_RECIPES) {
      setSharedRecipesCurrentPage(1);
    } else if (tabKey === TAB_KEYS.ARCHIVED_RECIPES) {
      setArchivedRecipesCurrentPage(1);
    }
  };

  const handleResetFilters = () => {
    if (!isFilterApplied) return false;
    setSelectedChemicals([]);
    setSelectedTypes([]);
    setIsFilterApplied(false);
    return true;
  };

  return (
    <div className="scrollable-container">
      <Button
        variant="outline-dark"
        size="sm"
        className={`${
          isFilterApplied ? "filter-icon-active" : "filter-icon"
        } pl-3 position-absolute end-0 rounded-0`}
        onClick={() => setShowFilter(!showFilter)}
        aria-label="Recipes Filter"
        title="Recipes Filter"
      >
        <i className="fa fa-filter" aria-hidden="true"></i>
      </Button>
      <div className="d-flex align-items-center gap-2">
        <h5 className="m-0">Chemical Recipes </h5>
        <Form.Group className="m-0">
          <Form.Select
            className="input-underline pb-0"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            size="sm"
            aria-label="Select Year"
          >
            {DateHelper.getYears().map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>
      <Row className="align-items-center justify-content-between">
        <Col className="my-2">
          <div className="d-flex gap-3 align-items-center">
            <Button
              variant="link"
              className="text-decoration-none p-0 d-flex align-items-center"
              onClick={() => setShowShareRecipe(true)}
              aria-label="Share Recipe"
              disabled={myRecipes.length === 0}
            >
              <i className="fa fa-share-alt me-1" aria-hidden="true"></i>
              <span>Share</span>
            </Button>
            <Button
              variant="link"
              className="text-decoration-none p-0 d-flex align-items-center"
              onClick={() => {
                dispatch(setEditedRecipe(null));
                setShowRecipeForm(true);
              }}
              aria-label="Create Recipe"
            >
              <i className="fa fa-plus-circle me-1" aria-hidden="true"></i>
              <span>Create</span>
            </Button>
          </div>
        </Col>
        <Col xs={12} md={6} lg={6} xl={4} className="position-relative my-2">
          <Form.Control
            type="text"
            placeholder={LABEL_SEARCH_RECIPE}
            className="input-underline pe-5"
            aria-label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search ? (
            <i
              className="fa fa-times filter-clear-search"
              onClick={() => setSearch("")}
            ></i>
          ) : (
            <i className="fa fa-search filter-search"></i>
          )}
        </Col>
      </Row>

      <Tabs
        id="controlled-tabs"
        activeKey={activeTab}
        onSelect={handleTabSelect}
        className="my-3"
      >
        <Tab eventKey={TAB_KEYS.OWNED_RECIPES} title="My Recipes">
          {isLoading || !recipes ? (
            <CardSkeleton numberOfCards={3} />
          ) : myRecipes.length === 0 ? (
            <EmptyChemicalRecipes setShowRecipeForm={setShowRecipeForm} />
          ) : (
            <div>
              <Row className="g-3">
                {(paginatedMyRecipes.length > 0
                  ? paginatedMyRecipes
                  : myRecipes
                ).map((recipe) => (
                  <Col key={recipe.id} sm={12} md={6} lg={6} xl={4}>
                    <RecipeCard recipe={recipe} />
                  </Col>
                ))}
              </Row>
              <CustomPagination
                currentPage={myRecipesCurrentPage}
                totalPages={Math.ceil(myRecipes.length / 9)}
                data={myRecipes}
                itemsPerPage={9}
                onPageChange={handleMyRecipesPageChange}
                onDataChange={setPaginatedMyRecipes}
              />
            </div>
          )}
        </Tab>
        <Tab
          eventKey={TAB_KEYS.SHARED_RECIPES}
          title={
            <>
              Shared<span className="d-none d-md-inline"> Recipes</span>
            </>
          }
        >
          {isLoading || !recipes ? (
            <CardSkeleton numberOfCards={3} />
          ) : sharedRecipes.length === 0 ? (
            <EmptyShare emptyText={NO_SHARED_RECIPES} />
          ) : (
            <div>
              <Row className="g-3">
                {(paginatedSharedRecipes.length > 0
                  ? paginatedSharedRecipes
                  : sharedRecipes
                ).map((recipe) => (
                  <Col key={recipe.id} sm={12} md={6} lg={6} xl={4}>
                    <RecipeCard recipe={recipe} />
                  </Col>
                ))}
              </Row>
              <CustomPagination
                currentPage={sharedRecipesCurrentPage}
                totalPages={Math.ceil(sharedRecipes.length / 9)}
                data={sharedRecipes}
                itemsPerPage={9}
                onPageChange={handleSharedRecipesPageChange}
                onDataChange={setPaginatedSharedRecipes}
              />
            </div>
          )}
        </Tab>
        <Tab
          eventKey={TAB_KEYS.ARCHIVED_RECIPES}
          title={
            <>
              Archived<span className="d-none d-md-inline"> Recipes</span>
            </>
          }
        >
          {isLoading || !recipes ? (
            <CardSkeleton numberOfCards={3} />
          ) : archivedRecipes.length === 0 ? (
            <EmptyArchive emptyText={NO_ARCHIVED_RECIPES} />
          ) : (
            <div>
              <Row className="g-3">
                {(paginatedArchivedRecipes.length > 0
                  ? paginatedArchivedRecipes
                  : archivedRecipes
                ).map((recipe) => (
                  <Col key={recipe.id} sm={12} md={6} lg={6} xl={4}>
                    <RecipeCard recipe={recipe} />
                  </Col>
                ))}
              </Row>
              <CustomPagination
                currentPage={archivedRecipesCurrentPage}
                totalPages={Math.ceil(archivedRecipes.length / 9)}
                data={archivedRecipes}
                itemsPerPage={9}
                onPageChange={handleArchivedRecipesPageChange}
                onDataChange={setPaginatedArchivedRecipes}
              />
            </div>
          )}
        </Tab>
      </Tabs>
      <RecipeFilter
        show={showFilter}
        handleClose={() => setShowFilter(false)}
        selectedChemicals={selectedChemicals}
        selectedTypes={selectedTypes}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />
      <RecipeForm
        mode={MODE_TYPES.ADD}
        show={showRecipeForm}
        onClose={() => setShowRecipeForm(false)}
      />
      <ShareRecipes
        type={SHARE_TYPES.MULTIPLE}
        recipes={recipes}
        showShareRecipe={showShareRecipe}
        onClose={() => setShowShareRecipe(false)}
      />
      {shareRecipeSuccess && (
        <SuccessPopup
          message={renderShareRecipeSuccess()}
          show={shareRecipeSuccess}
          handleClose={() => dispatch(setShareRecipeSuccess(false))}
        />
      )}
      {saveRecipeSuccess && (
        <SuccessPopup
          message={
            recipeMode === MODE_TYPES.ADD
              ? ADDED_RECIPE
              : savedRecipe?.newRecipe
              ? NEW_VERSION_FOR_RECIPE
              : UPDATED_RECIPE
          }
          value={
            savedRecipe.oldRecipe
              ? savedRecipe.oldRecipe.name
              : savedRecipe.name
          }
          show={saveRecipeSuccess}
          handleClose={handleSuccessClose}
        />
      )}
    </div>
  );
};

export default ChemicalRecipes;
